import { useTranslation } from 'react-i18next';
import AgyleDialog from '../../../../../components/modal/agyleDialog';
import { useEffect, useRef, useState } from 'react';
import API from '../../../../../app/api';
import { useDispatch } from 'react-redux';
import { setSnackbar } from '../../../../../store/reducers/globals';
import AgyleLoadingSpinner from '../../../../../components/other/agyleLoadingSpinner';
import { AgyleTable, createAgyleTableColumn } from '../../../../../components/table/AgyleTable';
import { IconButton, Typography } from '@mui/material';
import { AgyleActionIcon } from '../../../../../components/icons/agyle';
import { cloneDeep, filter } from 'lodash';
import MessageBox from '../../../../../util/messageBox';
import useOpenState from '../../../../../hooks/useOpenState';
import AgyleButton from '../../../../../components/ui/buttons/agyleButton';
import AddIcon from '@mui/icons-material/Add';
import AgyleTextField from '../../../../../components/ui/fields/agyleTextField';

const columnsTable = [
    createAgyleTableColumn('note', 'Note', 1, 'note', 'string', '', 'medium', 'left', true, true, 150, null),
    createAgyleTableColumn('action', 'Action', 5, null, 'action', '', 'medium', 'right', true, true, 150, null),
];

const MedicationNoteTemplateModal = ({ type, open, onClose, onSelect }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);

    // Add new template
    const [isNewNoteOpen, openNewNote, closeNewNote] = useOpenState(false, true);
    const [note, setNote] = useState('');

    // Delete template
    const [isModalOpen, openModal, closeModal] = useOpenState(false, true);
    const tempItem = useRef(null);

    useEffect(() => {
        if (open && type) {
            loadData();
        }
    }, [open, type]);

    const loadData = () => {
        setLoading(true);
        API.getPrescriptionNoteTemplates(type)
            .then((res) => {
                if (res?.__data) {
                    setTemplates(filter(res?.__data, (temp) => !temp?.deactivated));
                }
            })
            .catch((err) => {
                console.error(err);
                dispatch(setSnackbar({ type: 'Error', message: t('An error occurred when loading note templates.') }));
            })
            .finally(() => setLoading(false));
    };

    const handleSelect = () => {
        alert('TODO');
    };

    const renderCustomAction = (noteObj) => {
        const _onSelect = () => {
            console.log('NoteObj', noteObj);
            if (onSelect) {
                onSelect(noteObj);
            }
        };

        return (
            <IconButton size="small" onClick={_onSelect}>
                <AgyleActionIcon />
            </IconButton>
        );
    };

    const handleCloseModal = () => {
        closeModal();
        tempItem.current = null;
    };

    const handleDisableNote = () => {
        const clone = cloneDeep(tempItem.current);
        clone.deactivated = true;
        API.updatePrescriptionNoteTemplates(clone)
            .then(() => {
                dispatch(setSnackbar({ type: 'success', message: t('The note has been disabled.') }));
                loadData();
            })
            .catch((err) => {
                console.error(err);
                dispatch(setSnackbar({ type: 'error', message: t('An error occurred when saving note templates.') }));
            })
            .finally(() => {
                tempItem.current = null;
            });
        closeModal();
    };

    const handleAddNote = () => {
        const noteObj = {
            note,
            type,
            deactivated: false,
        };
        API.updatePrescriptionNoteTemplates(noteObj)
            .then(() => {
                dispatch(setSnackbar({ type: 'success', message: t('The note has been created.') }));
                loadData();
            })
            .catch((err) => {
                console.error(err);
                dispatch(setSnackbar({ type: 'error', message: t('An error occurred when saving note templates.') }));
            })
            .finally(() => {
                setNote('');
            });
        closeNewNote();
    };

    const openDeleteItem = (item) => {
        tempItem.current = item;
        openModal();
    };

    const closeNewTemplate = () => {
        closeNewNote();
        setNote('');
    };

    const renderInput = () => {
        return (
            <>
                <Typography>{t('Note template')}</Typography>

                <AgyleTextField value={note} onChange={(e) => setNote(e.target.value)} />
            </>
        );
    };

    return (
        <>
            <AgyleDialog
                onClose={onClose}
                open={open}
                title={t('Template')}
                closeButtonOnHeader
                width="sm"
                fullWidth
                onSave={handleSelect}
                buttons={[
                    <AgyleButton startIcon={<AddIcon />} onClick={openNewNote}>
                        {t('Add note')}
                    </AgyleButton>,
                ]}
            >
                <>
                    {loading && <AgyleLoadingSpinner />}

                    {!loading && (
                        <AgyleTable
                            columns={columnsTable}
                            data={templates}
                            disableFilter
                            deleteItem={openDeleteItem}
                            customActionItem={renderCustomAction}
                        />
                    )}
                </>
            </AgyleDialog>

            <MessageBox
                onClose={handleCloseModal}
                title={t('Confirmation')}
                haveAccept={{ yes: t('Submit'), no: t('Close') }}
                onAccept={handleDisableNote}
                message={t('Are you sure you want to delete this template?')}
                open={isModalOpen}
            />

            <MessageBox
                onClose={closeNewTemplate}
                title={t('Confirmation')}
                haveAccept={{ yes: t('Submit'), no: t('Close') }}
                onAccept={handleAddNote}
                messageObject={renderInput()}
                open={isNewNoteOpen}
            />
        </>
    );
};

export default MedicationNoteTemplateModal;
